import type { PresentationBrandAssetSlot, PresentationBrandResponse } from "@/api/admin/skills"

export const FRAME_SLOT_ID = "frame-logo"
export const DEFAULT_FRAME_FILENAME = "brand-logo.png"

export function slugFromFilename(filename: string): string {
  const stem = filename.replace(/\.[^.]+$/, "").toLowerCase()
  const slug = stem.replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")
  return slug.slice(0, 64) || "logo"
}

export function specHasMarkdown(markdown: string | null | undefined): boolean {
  return Boolean(markdown?.trim())
}

export function markdownLineCount(markdown: string): number {
  if (!markdown) {
    return 0
  }
  return markdown.split(/\r\n|\n|\r/).length
}

export function formatByteSize(bytes: number | undefined): string {
  if (!bytes || bytes <= 0) {
    return ""
  }
  if (bytes < 1024) {
    return `${bytes} B`
  }
  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)} KB`
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function pickFrameSlot(
  assets: Record<string, PresentationBrandAssetSlot>
): { id: string; slot: PresentationBrandAssetSlot } | null {
  const entries = Object.entries(assets)
  const byRole = entries.find(([, slot]) => slot.role === "frame_logo")
  if (byRole) {
    return { id: byRole[0], slot: byRole[1] }
  }
  if (FRAME_SLOT_ID in assets) {
    return { id: FRAME_SLOT_ID, slot: assets[FRAME_SLOT_ID] }
  }
  const required = entries.find(([, slot]) => slot.required)
  if (required) {
    return { id: required[0], slot: required[1] }
  }
  return null
}

export function extraLogoSlots(
  assets: Record<string, PresentationBrandAssetSlot>
): { id: string; slot: PresentationBrandAssetSlot }[] {
  const frame = pickFrameSlot(assets)
  return Object.entries(assets)
    .filter(([id]) => id !== frame?.id)
    .map(([id, slot]) => ({ id, slot }))
}

export function isFrameLogoUploaded(assets: Record<string, PresentationBrandAssetSlot>): boolean {
  return Boolean(pickFrameSlot(assets)?.slot.blob)
}

export type PresentationGenerationMode = "markdown" | "managed" | "ask"
export type PresentationBrandStatusKind = "live" | "incomplete" | "template" | "ask"

export function presentationGenerationMode(data: {
  generation_mode?: string
  enabled: boolean
  enforce_company_template: boolean
  spec_markdown: string
}): PresentationGenerationMode {
  const raw = data.generation_mode
  if (raw === "markdown" || raw === "managed" || raw === "ask") {
    return raw
  }
  if (data.enabled && specHasMarkdown(data.spec_markdown)) {
    return "markdown"
  }
  if (data.enforce_company_template) {
    return "managed"
  }
  return "ask"
}

export function presentationBrandStatus(
  data: Pick<
    PresentationBrandResponse,
    | "enabled"
    | "spec_markdown"
    | "complete"
    | "assets"
    | "generation_mode"
    | "enforce_company_template"
  >
): PresentationBrandStatusKind {
  const mode = presentationGenerationMode(data)
  if (mode === "markdown") {
    return data.complete ? "live" : "incomplete"
  }
  if (mode === "managed") {
    return "template"
  }
  return "ask"
}

export function presentationBrandChecklist(data: PresentationBrandResponse): {
  specSaved: boolean
  frameLogo: boolean
  enabled: boolean
  extraLogos: boolean
} {
  return {
    specSaved: specHasMarkdown(data.spec_markdown),
    frameLogo: isFrameLogoUploaded(data.assets),
    enabled: Boolean(data.enabled),
    extraLogos: extraLogoSlots(data.assets).some((item) => Boolean(item.slot.blob)),
  }
}

const NO_MANAGED_TEMPLATE_ERROR = /no managed PowerPoint template/i

function axiosResponse(error: unknown): { status?: number; data?: unknown } | undefined {
  if (!error || typeof error !== "object" || !("response" in error)) {
    return undefined
  }
  const response = (error as { response?: unknown }).response
  if (!response || typeof response !== "object") {
    return undefined
  }
  return response as { status?: number; data?: unknown }
}

export function isNoManagedTemplateError(error: unknown): boolean {
  const response = axiosResponse(error)
  if (response?.status === 404) {
    return true
  }
  const data = response?.data
  if (data && typeof data === "object" && typeof (data as { error?: unknown }).error === "string") {
    return NO_MANAGED_TEMPLATE_ERROR.test((data as { error: string }).error)
  }
  return false
}

export function generationModeWhenDisablingMarkdown(
  hasCompanyTemplate: boolean
): "managed" | "ask" {
  return hasCompanyTemplate ? "managed" : "ask"
}
